import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetOperatorInvestorByEmailQuery } from 'src/generated/graphql-types';
import { RootState } from '../config/rootReducer';

type UserLegalEntityGql =
  GetOperatorInvestorByEmailQuery['findOperatorInvestorByEmail']['userLegalEntities'][number];

//defines types
type InitialStateType = {
  selectedUserEntityID: string | null;
  selectedInvestorID: string | null;
  allUserEntities: UserLegalEntityGql[];
  loadedInvestors: { id: string; email: string }[];
};

//set initial values
const initialState: InitialStateType = {
  selectedUserEntityID: null,
  selectedInvestorID: null,
  allUserEntities: [],
  loadedInvestors: [],
};

// Define slices
const userEntitySlice = createSlice({
  name: 'userEntity',
  initialState,
  reducers: {
    setSelectedUserEntityID(state, { payload }: PayloadAction<string | null>) {
      state['selectedUserEntityID'] = payload;
    },
    setSelectedInvestorID(state, { payload }: PayloadAction<string | null>) {
      state['selectedInvestorID'] = payload;
    },
    setAllUserEntities(state, { payload }: PayloadAction<UserLegalEntityGql[]>) {
      state['allUserEntities'] = payload;
    },
    setLoadedInvestors(state, { payload }: PayloadAction<{ id: string; email: string }[]>) {
      state['loadedInvestors'] = payload;
    },
    cleanUpUserEntitySlice(state) {
      Object.entries(initialState).forEach(([key, value]) => {
        state[key] = value;
      });
    },
  },
});

const selectSelectedUserEntityID = (state: RootState) => state.userEntity.selectedUserEntityID;
const selectSelectedInvestorID = (state: RootState) => state.userEntity.selectedInvestorID;
const selectAllUserEntities = (state: RootState) => state.userEntity.allUserEntities;

// define computed data
export const selectUserEntity: (state: RootState) => UserLegalEntityGql | null = createSelector(
  selectAllUserEntities,
  selectSelectedUserEntityID,
  (allEntities, selectedUserEntityID) => {
    const entity = allEntities.find(e => e.id === selectedUserEntityID);
    return entity ?? null;
  },
);
export const selectInvestorEmails: (state: RootState) => string[] = createSelector(
  selectUserEntity,
  selectedUserEntity => {
    if (!selectedUserEntity) return [];
    return selectedUserEntity.investors.map(i => i.email);
  },
);
export const selectViewerEmails: (state: RootState) => string[] = createSelector(
  selectUserEntity,
  selectedUserEntity => {
    if (!selectedUserEntity) return [];
    return selectedUserEntity.viewers.map(i => i.email);
  },
);
export const selectInvestorEntities: (state: RootState) => UserLegalEntityGql[] = createSelector(
  selectAllUserEntities,
  selectSelectedInvestorID,
  (allUserEntities, selectedInvestorID) => {
    return allUserEntities.filter(
      entity =>
        entity.investorIDs.includes(selectedInvestorID) ||
        entity.viewerIDs.includes(selectedInvestorID), //todo idan - should add prospectss
    );
  },
);

//export payloads
export const {
  setSelectedInvestorID,
  setSelectedUserEntityID,
  setAllUserEntities,
  setLoadedInvestors,
  cleanUpUserEntitySlice,
} = userEntitySlice.actions;

export default userEntitySlice.reducer;
